import React, { useState, useEffect, useCallback, useRef } from 'react';
import './AnimatedCards.css';

/* macOS-style cursor SVG */
const CursorSVG = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
    <path d="M5 3l14 8-6 2-3 7z" fill="white" stroke="#444" strokeWidth="1.2" />
  </svg>
);

/* Small confetti burst component */
const ConfettiBurst = ({ x, y }) => {
  const colors = ['#2563EB', '#059669', '#60A5FA', '#f59e0b', '#ef4444', '#ec4899'];
  const pieces = Array.from({ length: 10 }, (_, i) => {
    const angle = (i / 10) * 2 * Math.PI;
    const dist = 18 + Math.random() * 16;
    return {
      color: colors[i % colors.length],
      tx: Math.cos(angle) * dist,
      ty: Math.sin(angle) * dist,
    };
  });

  return (
    <div className="anim-confetti" style={{ left: x, top: y }}>
      {pieces.map((p, i) => (
        <div
          key={i}
          className="anim-confetti-piece"
          style={{
            background: p.color,
            '--tx': `${p.tx}px`,
            '--ty': `${p.ty}px`,
          }}
        />
      ))}
    </div>
  );
};

const AnimatedCards = () => {
  /* ---- Cursor state ---- */
  const [cursorPos, setCursorPos] = useState({ x: '8%', y: '18%' });
  const [cursorVisible, setCursorVisible] = useState(false);
  const [cursorClicking, setCursorClicking] = useState(false);
  const [cursorSpeed, setCursorSpeed] = useState(''); // '', 'fast', 'slow', 'sweep'

  /* ---- Card 1 state ---- */
  const [gmailBright, setGmailBright] = useState(false);
  const [gmailTooltip, setGmailTooltip] = useState(false);
  const [outlookHovered, setOutlookHovered] = useState(false);
  const [outlookLinked, setOutlookLinked] = useState(false);
  const [outlookRing, setOutlookRing] = useState(false);
  const [addAccountHovered, setAddAccountHovered] = useState(false);

  /* ---- Card 2 state ---- */
  const [fileRowHighlight, setFileRowHighlight] = useState(false);
  const [fileRemoveVisible, setFileRemoveVisible] = useState(false);
  const [fileRemoveRed, setFileRemoveRed] = useState(false);
  const [tagsShowX, setTagsShowX] = useState(false);
  const [pmDeepened, setPmDeepened] = useState(false);
  const [salaryDropdown, setSalaryDropdown] = useState(false);
  const [salaryHoveredItem, setSalaryHoveredItem] = useState(null);
  const [salaryText, setSalaryText] = useState('$150k+');
  const [showInput, setShowInput] = useState(false);
  const [typingText, setTypingText] = useState('');
  const [newTag, setNewTag] = useState(null);
  const [addHovered, setAddHovered] = useState(false);

  /* ---- Card 3 state ---- */
  const [card3Hovered, setCard3Hovered] = useState(false);
  const [card3BadgePulse, setCard3BadgePulse] = useState(false);
  const [googleRowActive, setGoogleRowActive] = useState(false);
  const [googlePillsBounce, setGooglePillsBounce] = useState(false);
  const [googlePressed, setGooglePressed] = useState(false);
  const [googleDetail, setGoogleDetail] = useState(false);
  const [stripeRowActive, setStripeRowActive] = useState(false);
  const [interviewFilled, setInterviewFilled] = useState(false);
  const [interviewLineSolid, setInterviewLineSolid] = useState(false);
  const [confetti, setConfetti] = useState(null);

  /* ---- Pulse wave ---- */
  const [pulseCard, setPulseCard] = useState(null);
  const [activeCard, setActiveCard] = useState(null);

  const timersRef = useRef([]);

  const clearAllTimers = useCallback(() => {
    timersRef.current.forEach(t => clearTimeout(t));
    timersRef.current = [];
  }, []);

  const schedule = useCallback((fn, delay) => {
    const id = setTimeout(fn, delay);
    timersRef.current.push(id);
    return id;
  }, []);

  /* Reset everything to initial state */
  const resetAll = useCallback(() => {
    setCursorVisible(false);
    setCursorClicking(false);
    setCursorSpeed('');
    setCursorPos({ x: '8%', y: '18%' });
    setGmailBright(false);
    setGmailTooltip(false);
    setOutlookHovered(false);
    setOutlookLinked(false);
    setOutlookRing(false);
    setAddAccountHovered(false);
    setFileRowHighlight(false);
    setFileRemoveVisible(false);
    setFileRemoveRed(false);
    setTagsShowX(false);
    setPmDeepened(false);
    setSalaryDropdown(false);
    setSalaryHoveredItem(null);
    setSalaryText('$150k+');
    setShowInput(false);
    setTypingText('');
    setNewTag(null);
    setAddHovered(false);
    setCard3Hovered(false);
    setCard3BadgePulse(false);
    setGoogleRowActive(false);
    setGooglePillsBounce(false);
    setGooglePressed(false);
    setGoogleDetail(false);
    setStripeRowActive(false);
    setInterviewFilled(false);
    setInterviewLineSolid(false);
    setConfetti(null);
    setPulseCard(null);
    setActiveCard(null);
  }, []);

  const doClick = useCallback(() => {
    setCursorClicking(true);
    schedule(() => setCursorClicking(false), 180);
  }, [schedule]);

  const runAnimation = useCallback(() => {
    clearAllTimers();
    resetAll();

    // ==================== PHASE 1 — Card 1 (0–5.5s) ====================

    // 0s – cursor fades in top-left of card 1
    schedule(() => {
      setCursorPos({ x: '8%', y: '18%' });
      setCursorVisible(true);
      setActiveCard(1);
    }, 200);

    // 1.0s – hover Gmail pill, brighten, tooltip
    schedule(() => {
      setCursorPos({ x: '25%', y: '28%' });
      setGmailBright(true);
    }, 1000);
    schedule(() => setGmailTooltip(true), 1300);

    // 2.0s – move to Outlook pill (lower position)
    schedule(() => {
      setGmailBright(false);
      setGmailTooltip(false);
      setCursorPos({ x: '25%', y: '48%' });
      setOutlookHovered(true);
    }, 2000);

    // 3.0s – click Outlook connect
    schedule(() => {
      doClick();
    }, 3000);

    // 3.2s – morph to linked
    schedule(() => {
      setOutlookLinked(true);
      setOutlookRing(true);
      setOutlookHovered(false);
    }, 3200);

    // 3.7s – ring off
    schedule(() => setOutlookRing(false), 3700);


    // 4.5s – start exiting to card 2
    schedule(() => {
      setAddAccountHovered(false);
      setCursorSpeed('slow');
      setCursorPos({ x: '48%', y: '24%' });
    }, 4500);

    // 5.5s – fade card 1 interaction states
    schedule(() => {
      setCursorSpeed('');
    }, 5500);

    // ==================== PHASE 2 — Card 2 (5.5–11.5s) ====================

    // 5.8s – arrive at resume.pdf row
    schedule(() => {
      setActiveCard(2);
      setCursorPos({ x: '55%', y: '24%' });
      setFileRowHighlight(true);
      setFileRemoveVisible(true);
    }, 5800);

    // 6.4s – hover X button
    schedule(() => {
      setCursorPos({ x: '62%', y: '32%' });
      setFileRemoveRed(true);
    }, 6400);


    // 8.0s – move to salary pill
    schedule(() => {
      setPmDeepened(false);
      setCursorPos({ x: '55%', y: '43%' });
    }, 8000);

    // 8.5s – click salary to open dropdown
    schedule(() => {
      doClick();
    }, 8500);
    schedule(() => {
      setSalaryDropdown(true);
    }, 8900);

    // 9.0s – hover $200k+
    schedule(() => {
      setCursorPos({ x: '52%', y: '78%' });
      setSalaryHoveredItem('200k');
    }, 9000);

    // 9.5s – select $200k+
    schedule(() => {
      doClick();
    }, 9500);
    schedule(() => {
      setSalaryText('$200k+');
      setSalaryDropdown(false);
      setSalaryHoveredItem(null);
    }, 9800);

    // 10.0s – move to + Add
    schedule(() => {
      setTagsShowX(false);
      setCursorPos({ x: '52%', y: '66%' });
      setAddHovered(true);
    }, 10000);

    // 10.4s – click + Add
    schedule(() => {

    }, 10400);
    schedule(() => {
      doClick();
      setAddHovered(false);
      setShowInput(true);
    }, 10600);

    // 10.8–11.4s – simulated typing
    const typeWord = 'San Fran';
    typeWord.split('').forEach((char, i) => {
      schedule(() => setTypingText(prev => prev + char), 10800 + i * 80);
    });

    // 11.5s – autocomplete
    schedule(() => setTypingText('San Francisco'), 11500);

    // 11.8s – confirm tag
    schedule(() => {
      setShowInput(false);
      setTypingText('');
      setNewTag('San Francisco');
    }, 11800);

    // 12.0s – exit to Card 3
    schedule(() => {
      setCursorSpeed('slow');
      setCursorPos({ x: '80%', y: '22%' });
    }, 12000);

    // ==================== PHASE 3 — Card 3 (12.0–18.0s) ====================

    // 12.5s – arrive at card 3, card lifts
    schedule(() => {
      setActiveCard(3);
      setCursorSpeed('');
      setCard3Hovered(true);
      setCard3BadgePulse(true);
    }, 12500);

    schedule(() => setCard3BadgePulse(false), 13000);

    // 13.0s – hover Google row
    schedule(() => {
      setCursorPos({ x: '85%', y: '30%' });
      setGoogleRowActive(true);
      setGooglePillsBounce(true);
    }, 13000);

    schedule(() => setGooglePillsBounce(false), 13500);

    // 14.5s – click Google row (longer hover)
    schedule(() => {
      doClick();
      setGooglePressed(true);
    }, 14500);

    schedule(() => setGooglePressed(false), 14700);

    // 14.8s – detail panel
    schedule(() => {
      setGoogleDetail(true);
    }, 14800);

    // 15.5s – collapse detail, move to Stripe
    schedule(() => {
      setGoogleDetail(false);
      setGoogleRowActive(false);
      setCursorPos({ x: '85%', y: '45%' });
      setStripeRowActive(false);
    }, 15500);

    // 16.5s – cursor sweep left
    schedule(() => {
      setCursorSpeed('sweep');
      setCursorPos({ x: '8%', y: '40%' });
    }, 16500);

    // 16.7–17.3s – sequential card pulses
    schedule(() => setPulseCard(3), 16700);
    schedule(() => setPulseCard(2), 17000);
    schedule(() => setPulseCard(1), 17300);
    schedule(() => setPulseCard(null), 17600);

    // 17.5s – fade out cursor
    schedule(() => {
      setCursorVisible(false);
    }, 17500);

    // 18.0s – done, keep final state
  }, [clearAllTimers, resetAll, doClick, schedule]);

  const containerRef = useRef(null);
  const hasStarted = useRef(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted.current) {
          hasStarted.current = true;
          runAnimation();
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      clearAllTimers();
    };
  }, [runAnimation, clearAllTimers]);

  /* ---- Cursor class ---- */
  const cursorClass = [
    'anim-cursor',
    cursorVisible ? 'visible' : '',
    cursorClicking ? 'clicking' : '',
    cursorSpeed,
  ].filter(Boolean).join(' ');

  return (
    <div className="anim-cards-container" ref={containerRef}>
      {/* Animated cursor */}
      <div
        className={cursorClass}
        style={{ left: cursorPos.x, top: cursorPos.y }}
      >
        <CursorSVG />
      </div>

      {/* Confetti */}
      {confetti && <ConfettiBurst x={confetti.x} y={confetti.y} />}

      <div className="anim-grid">
        {/* ========== Card 1 — Connected Accounts ========== */}
        <div className={`anim-card${pulseCard === 1 ? ' pulse' : ''}${activeCard === 1 ? ' active' : ''}`}>
          <div className="anim-card-title">Connected Accounts</div>
          <div className="anim-card-body">
            {/* Gmail row */}
            <div className="anim-row anim-row-done">
              <span className="anim-service-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <rect x="1" y="3" width="22" height="18" rx="3" fill="none" stroke="#EA4335" strokeWidth="1.5"/>
                  <path d="M22 6L12 13 2 6" fill="none" stroke="#EA4335" strokeWidth="1.5"/>
                </svg>
              </span>
              <span className="anim-service-name">Gmail</span>
              <span className={`anim-pill anim-pill-green${gmailBright ? ' brightened' : ''}`} style={{ position: 'relative' }}>
                <span className="anim-pill-icon">&#10003;</span> linked
                <div className={`anim-tooltip${gmailTooltip ? ' visible' : ''}`}>
                  Connected since Mar 12
                </div>
              </span>
            </div>
            <div className="anim-divider" />

            {/* Outlook row */}
            <div className={`anim-row anim-outlook-row${outlookLinked ? ' connected' : ''}${outlookHovered ? ' anim-row-highlight' : ''}`}>
              <span className="anim-service-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <rect x="1" y="3" width="22" height="18" rx="3" fill="none" stroke="#0078D4" strokeWidth="1.5"/>
                  <path d="M22 6L12 13 2 6" fill="none" stroke="#0078D4" strokeWidth="1.5"/>
                </svg>
              </span>
              <span className="anim-service-name">Outlook</span>
              {outlookLinked ? (
                <span className="anim-pill anim-pill-linked" style={{ position: 'relative' }}>
                  <span className="anim-pill-icon">&#10003;</span> linked
                  <div className={`anim-success-ring${outlookRing ? ' active' : ''}`} />
                </span>
              ) : (
                <span className={`anim-pill anim-pill-gray-btn${outlookHovered ? ' hovered' : ''}`}>
                  connect <span className="anim-pill-chevron">&rsaquo;</span>
                </span>
              )}
            </div>
            <div className="anim-divider" />

            {/* Yahoo row */}
            <div className="anim-row">
              <span className="anim-service-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <rect x="1" y="3" width="22" height="18" rx="3" fill="none" stroke="#6001D2" strokeWidth="1.5"/>
                  <path d="M22 6L12 13 2 6" fill="none" stroke="#6001D2" strokeWidth="1.5"/>
                </svg>
              </span>
              <span className="anim-service-name">Yahoo Mail</span>
              <span className="anim-pill anim-pill-gray-btn">connect <span className="anim-pill-chevron">&rsaquo;</span></span>
            </div>
            <div className="anim-divider" />

            {/* Add account row */}
            <div className={`anim-row anim-row-ghost${addAccountHovered ? ' hovered' : ''}`}>
              <span className="anim-service-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9" stroke="#D1D5DB" strokeWidth="1.5" fill="none"/>
                  <path d="M12 8v8M8 12h8" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </span>
              <span className="anim-service-name" style={{ color: '#9CA3AF' }}>Add account</span>
            </div>
          </div>
        </div>

        {/* ========== Card 2 — Your Profile ========== */}
        <div className={`anim-card${pulseCard === 2 ? ' pulse' : ''}${activeCard === 2 ? ' active' : ''}`}>
          <div className="anim-card-title">Your Profile</div>
          <div className="anim-card-body">
            {/* File row */}
            <div className={`anim-file-row${fileRowHighlight ? ' highlight' : ''}`}>
              <span className="anim-file-icon-svg">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  <polyline points="14 2 14 8 20 8" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
              </span>
              <span className="anim-file-name">resume.pdf</span>
              <span className={`anim-file-remove${fileRemoveVisible ? ' visible' : ''}${fileRemoveRed ? ' red' : ''}`}>&times;</span>
            </div>

            {/* Tags */}
            <div className="anim-tags-row">
              <span className={`anim-pill anim-pill-blue${pmDeepened ? ' deepened' : ''}`}>
                Product Manager <span className={`anim-tag-x${tagsShowX ? ' visible' : ''}`}>&times;</span>
              </span>
              <span className="anim-pill anim-pill-blue" style={{ position: 'relative' }}>
                {salaryText} <span className={`anim-tag-x${tagsShowX ? ' visible' : ''}`}>&times;</span>
                <div className={`anim-dropdown${salaryDropdown ? ' visible' : ''}`}>
                  <div className={`anim-dropdown-item${salaryHoveredItem === '100k' ? ' hovered' : ''}`}>$100k+</div>
                  <div className={`anim-dropdown-item${salaryHoveredItem === '150k' ? ' hovered' : ''}`}>$150k+</div>
                  <div className={`anim-dropdown-item${salaryHoveredItem === '200k' ? ' hovered' : ''}`}>$200k+</div>
                  <div className={`anim-dropdown-item${salaryHoveredItem === '250k' ? ' hovered' : ''}`}>$250k+</div>
                </div>
              </span>
              <span className="anim-pill anim-pill-blue">
                Remote <span className={`anim-tag-x${tagsShowX ? ' visible' : ''}`}>&times;</span>
              </span>
              <span className="anim-pill anim-pill-blue">
                Full-time <span className={`anim-tag-x${tagsShowX ? ' visible' : ''}`}>&times;</span>
              </span>
              <span className="anim-pill anim-pill-blue">
                Startup <span className={`anim-tag-x${tagsShowX ? ' visible' : ''}`}>&times;</span>
              </span>
              <span className="anim-pill anim-pill-blue">
                Software Engineer <span className={`anim-tag-x${tagsShowX ? ' visible' : ''}`}>&times;</span>
              </span>
              {showInput ? (
                <span className="anim-tag-input">
                  {typingText}<span className="typing-cursor" />
                </span>
              ) : newTag ? (
                <span className="anim-pill anim-pill-blue">{newTag}</span>
              ) : (
                <span className={`anim-pill anim-pill-add${addHovered ? ' hovered' : ''}`}>+ Add</span>
              )}
            </div>
          </div>
        </div>

        {/* ========== Card 3 — Applications ========== */}
        <div className={`anim-card${card3Hovered ? ' hovered' : ''}${pulseCard === 3 ? ' pulse' : ''}${activeCard === 3 ? ' active' : ''}`}>
          <div className="anim-card-title">
            Applications{' '}
            <span className={`anim-count-badge${card3BadgePulse ? ' pulse' : ''}`}>3</span>
          </div>
          <div className="anim-card-body">
            {/* Google row */}
            <div className={`anim-row${googleRowActive ? ' anim-row-active' : ''}${googlePressed ? ' pressed' : ''}`}>
              <span className="anim-company-logo">G</span>
              <span className="anim-company-name">Google</span>
              <div className="anim-pills-group">
                <span className={`anim-pill anim-pill-green${googlePillsBounce ? ' bounce' : ''}`}>applied</span>
                <span className={`anim-pill anim-pill-blue${googlePillsBounce ? ' bounce' : ''}`}>email sent</span>
              </div>
              <span className={`anim-row-chevron${googleRowActive ? ' anim-chevron-active' : ''}`}>&rsaquo;</span>
            </div>

            {/* Google detail panel */}
            <div className={`anim-detail-panel${googleDetail ? ' expanded' : ''}`}>
              Applied Mar 8 &middot; Email sent Mar 9 &middot; Follow-up Mar 15
            </div>

            <div className="anim-divider" />

            {/* Stripe row */}
            <div className={`anim-row${stripeRowActive ? ' anim-row-active' : ''}`}>
              <span className="anim-company-logo">S</span>
              <span className="anim-company-name">Stripe</span>
              <div className="anim-pills-group">
                <span className="anim-pill anim-pill-green">applied</span>
                <span className="anim-pill anim-pill-blue">email sent</span>
              </div>
              <span className="anim-row-chevron">&rsaquo;</span>
            </div>
            <div className="anim-divider" />

            {/* Meta row */}
            <div className="anim-row">
              <span className="anim-company-logo">M</span>
              <span className="anim-company-name">Meta</span>
              <div className="anim-pills-group">
                <span className="anim-pill anim-pill-green">applied</span>
                <span className="anim-pill anim-pill-blue">email sent</span>
              </div>
              <span className="anim-row-chevron">&rsaquo;</span>
            </div>

            {/* Pipeline */}
            <div className="anim-pipeline-divider" />
            <div className="anim-pipeline">
              <div className="anim-pipeline-step">
                <div className="anim-pip-dot anim-pip-green" />
                <span className="anim-pip-label">Applied</span>
              </div>
              <div className="anim-pip-line anim-pip-line-gradient" />
              <div className="anim-pipeline-step">
                <div className="anim-pip-dot anim-pip-blue" />
                <span className="anim-pip-label">Email Sent</span>
              </div>
              <div className={`anim-pip-line anim-pip-line-dashed${interviewLineSolid ? ' solid-purple' : ''}`} />
              <div className="anim-pipeline-step">
                <div className={`anim-pip-dot anim-pip-hollow${interviewFilled ? ' filled-purple' : ''}`} />
                <span className="anim-pip-label">Interview</span>
              </div>
              <div className="anim-pip-line anim-pip-line-dashed" />
              <div className="anim-pipeline-step">
                <div className="anim-pip-dot anim-pip-hollow" />
                <span className="anim-pip-label">Offer</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnimatedCards;
